package org.example.abigail

import androidx.compose.foundation.layout.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.ui.unit.dp
import features.booking.Boleto
import features.booking.PasajeroInfo
import kotlinx.browser.window
import features.booking.generarHtmlBoleto
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.FirebaseOptions
import dev.gitlive.firebase.initialize
import kotlinx.browser.localStorage
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.get
import org.w3c.dom.set

class JsPlatform: Platform {
    override val name: String = "Web with Kotlin/JS"
}

actual fun getPlatform(): Platform = JsPlatform()

actual fun imprimirBoleto(pasajero: PasajeroInfo, boleto: Boleto, total: Int) {
    val contenido = generarHtmlBoleto(pasajero, boleto, total)

    // FIX #18: Usar iframe oculto en vez de window.open para evitar popup blocker
    val iframe = window.document.createElement("iframe") as org.w3c.dom.HTMLIFrameElement
    iframe.style.position = "fixed"
    iframe.style.width = "0"
    iframe.style.height = "0"
    iframe.style.border = "none"
    iframe.style.left = "-9999px"
    window.document.body?.appendChild(iframe)

    val iframeDoc = iframe.contentWindow?.document
    iframeDoc?.open()
    iframeDoc?.write(contenido)
    iframeDoc?.close()

    window.setTimeout({
        try {
            iframe.contentWindow?.print()
        } catch (e: Exception) {
            // Fallback: abrir en nueva pestaña si el iframe falla
            val fallback = window.open("", "_blank")
            fallback?.document?.write(contenido)
            fallback?.document?.close()
            window.setTimeout({ fallback?.print() }, 300)
        }
        // Limpiar iframe después de imprimir
        window.setTimeout({
            window.document.body?.removeChild(iframe)
        }, 1000)
    }, 400)
}

// Checkout en la MISMA pestaña (flujo estándar de Stripe). Con window.open quedaban dos
// pestañas: la original congelada en el formulario -y con los datos del pasajero a la vista
// aunque el pago ya se hubiera cobrado- y la del Checkout mostrando el éxito.
// Al volver de Stripe la página se recarga, así que el formulario queda limpio solo.
actual fun abrirVentanaPago(url: String) {
    window.location.href = url
}

actual fun cerrarVentanaPago() { /* no-op: ya no se abre pestaña aparte */ }

// Sin window.open no hay pestaña hija: el aviso de éxito lo muestra esta misma pestaña.
actual fun esPestanaDePago(): Boolean = false

actual fun cerrarPestanaActual() {
    try { window.close() } catch (_: Throwable) {}
}

actual fun recargarPagina() {
    try {
        // Sin el query de Stripe: recargar con ?pago=exitoso reabriría el aviso.
        window.location.replace(window.location.origin + window.location.pathname)
    } catch (_: Throwable) {}
}

actual fun abrirHtmlParaImprimir(html: String) {
    // Mismo patrón de iframe oculto para imprimir/guardar como PDF desde el navegador.
    val iframe = window.document.createElement("iframe") as org.w3c.dom.HTMLIFrameElement
    iframe.style.position = "fixed"; iframe.style.width = "0"; iframe.style.height = "0"
    iframe.style.border = "none"; iframe.style.left = "-9999px"
    window.document.body?.appendChild(iframe)
    val doc = iframe.contentWindow?.document
    doc?.open(); doc?.write(html); doc?.close()

    // El QR (api.qrserver.com) y el logo (Firebase Storage) son imágenes REMOTAS. Con un
    // setTimeout fijo se imprimía antes de que cargaran y el PDF salía sin QR ni logo.
    // Se espera a que todas terminen (cargadas o no), con tope por si alguna nunca responde.
    fun imprimir() {
        try { iframe.contentWindow?.print() } catch (e: Throwable) {
            val fb = window.open("", "_blank"); fb?.document?.write(html); fb?.document?.close()
            window.setTimeout({ fb?.print() }, 500)
        }
        window.setTimeout({ try { window.document.body?.removeChild(iframe) } catch (_: Throwable) {} }, 1000)
    }

    val inicio = kotlin.js.Date.now()
    fun esperarImagenes() {
        val imgs = doc?.images
        val total = imgs?.length ?: 0
        var listas = 0
        for (i in 0 until total) {
            val img = imgs?.item(i) as? org.w3c.dom.HTMLImageElement ?: continue
            // complete = cargada o fallida; en ambos casos ya no va a cambiar el layout.
            if (img.complete) listas++
        }
        val vencido = (kotlin.js.Date.now() - inicio) > 8000.0
        if (listas >= total || vencido) imprimir() else window.setTimeout({ esperarImagenes() }, 120)
    }
    window.setTimeout({ esperarImagenes() }, 100)
}

/**
 * Muestra los boletos EN la página, encima del canvas de Compose.
 *
 * Compose/JS pinta sobre un canvas; no se puede insertar este HTML "dentro" de la UI de Compose.
 * Se monta como capa fija por arriba, que es justo lo que queremos: el pasajero ve solo sus
 * boletos y les toma captura sin nada más en pantalla.
 *
 * La URL queda en ?boletos={sessionId} (sin recargar) para que pueda volver o guardarla.
 */
actual fun mostrarBoletosEnPagina(html: String, sessionId: String) {
    // Se REEMPLAZA el documento en vez de superponer una capa: Compose/JS dibuja su UI fuera
    // del DOM normal, así que un div encima resultaba invisible (medía 0x0). Reescribir el
    // documento es determinista y deja la página de boletos sola en pantalla, que es
    // justamente lo que se quiere para tomarle captura.
    //
    // La compra ya terminó: la app se recarga limpia al volver al inicio.
    try {
        // Primero la URL: si el usuario recarga, vuelve a los boletos y no al inicio.
        if (sessionId.isNotBlank()) {
            window.history.replaceState(null, "", "?boletos=$sessionId")
        }
        val doc = window.document
        doc.open()
        doc.write(html)
        doc.close()
    } catch (e: Throwable) {
        // Si el navegador bloquea la reescritura, abrir en pestaña nueva como respaldo.
        try {
            val w = window.open("", "_blank")
            w?.document?.open(); w?.document?.write(html); w?.document?.close()
        } catch (_: Throwable) {}
    }
}

/** Lee ?boletos=... de la URL (para reabrir los boletos al volver a entrar). */
actual fun leerSessionIdBoletos(): String {
    val search = window.location.search
    if (search.isBlank()) return ""
    val params = search.removePrefix("?").split("&")
    return params.firstOrNull { it.startsWith("boletos=") }?.substringAfter("=") ?: ""
}

actual fun formatMillisToDate(millis: Long): String {
    // DatePicker da millis a medianoche UTC, usar UTC para no perder/ganar un día por timezone
    // Sumar 12h para evitar edge cases de horario de verano
    val date = kotlin.js.Date(millis.toDouble() + 43200000.0) // +12h safety offset
    val year = date.getUTCFullYear()
    val month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    val day = date.getUTCDate().toString().padStart(2, '0')
    return "$year-$month-$day"
}

actual fun formatMillisToTime(millis: Long): String {
    val date = kotlin.js.Date(millis.toDouble())
    val hours = date.getHours().toString().padStart(2, '0')
    val minutes = date.getMinutes().toString().padStart(2, '0')
    val seconds = date.getSeconds().toString().padStart(2, '0')
    return "$hours:$minutes:$seconds"
}

actual fun inicializarFirebase() {
    Firebase.initialize(
        options = FirebaseOptions(
            apiKey = "AIzaSyCmEhc4OB_hRFfGY6ikFXD4VOZwMvI47FA",
            authDomain = "smartvan-38132.firebaseapp.com",
            databaseUrl = "https://smartvan-38132-default-rtdb.firebaseio.com",
            projectId = "smartvan-38132",
            storageBucket = "smartvan-38132.firebasestorage.app",
            applicationId = "1:626527596089:web:f060ae97f6bc101b4e27e8",
        )
    )
}
actual fun obtenerFechaHoy(): String {
    val date = kotlin.js.Date()
    val year = date.getFullYear()
    val month = (date.getMonth() + 1).toString().padStart(2, '0')
    val day = date.getDate().toString().padStart(2, '0')
    return "$year-$month-$day"
}

actual fun obtenerMinutosActuales(): Int {
    val date = kotlin.js.Date()
    return (date.getHours() * 60) + date.getMinutes()
}
actual fun calcularFechaFutura(fechaBase: String, diasSumar: Int): String {
    val partes = fechaBase.split("-")
    val year = partes[0].toInt()
    val month = partes[1].toInt() - 1
    val day = partes[2].toInt()

    val date = kotlin.js.Date(kotlin.js.Date.UTC(year, month, day))
    val milisegundosExtra = diasSumar * 24.0 * 60.0 * 60.0 * 1000.0
    val nuevaFecha = kotlin.js.Date(date.getTime() + milisegundosExtra)

    val y = nuevaFecha.getUTCFullYear()
    val m = (nuevaFecha.getUTCMonth() + 1).toString().padStart(2, '0')
    val d = nuevaFecha.getUTCDate().toString().padStart(2, '0')

    return "$y-$m-$d"
}

    actual fun obtenerMilisegundosActuales(): Long {
        return kotlin.js.Date().getTime().toLong()
        }

actual fun registrarTokenPush(uid: String) {
    // Web: Se implementará con FCM Web + Service Worker en el futuro
}

// FIX #20: Browser back button support
actual fun registrarOnBackNavigation(callback: () -> Unit) {
    window.onpopstate = { callback() }
}

actual fun pushBrowserState(screenName: String) {
    window.history.pushState(screenName, "")
}

// session_id del Checkout — se lee ANTES de limpiar la URL. Lo cacheamos porque
// leerYLimpiarQueryPago borra el query.
private var sessionIdCache: String = ""

// Lee ?pago=... del query, limpia la URL y devuelve el valor.
// Devuelve "" si no hay query o si es una plataforma nativa.
actual fun leerYLimpiarQueryPago(): String {
    return try {
        val search = window.location.search
        if (search.isBlank() || !search.contains("pago=")) return ""
        val params = search.trimStart('?').split("&")
        val valor = params.firstOrNull { it.startsWith("pago=") }?.substringAfter("=") ?: ""
        // Cachear session_id antes de limpiar la URL.
        sessionIdCache = params.firstOrNull { it.startsWith("session_id=") }?.substringAfter("=") ?: ""
        // Limpiar URL para que el usuario no vea el query
        window.history.replaceState(null, "", window.location.pathname)
        valor
    } catch (e: Exception) { "" }
}

actual fun leerSessionIdCheckout(): String = sessionIdCache

actual fun obtenerIdSesionPersistente(): String {
    val existente = localStorage["smartvan_session_id"]
    if (existente != null) return existente

    val nuevo = "CLIENT_${kotlin.random.Random.nextInt(10000, 99999)}_${kotlin.js.Date.now().toLong()}"
    localStorage["smartvan_session_id"] = nuevo
    return nuevo


}

actual fun abrirMapaRastreo(ruta: String, fecha: String, horario: String) {
    val rutaEscaped = ruta.replace("'", "\\'")
    val fechaEscaped = fecha.replace("'", "\\'")
    val horarioEscaped = horario.replace("'", "\\'")
    val htmlMapa = """
<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>SmartVan - Rastreo en Vivo</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<script src="https://www.gstatic.com/firebasejs/10.7.0/firebase-app-compat.js"></script>
<script src="https://www.gstatic.com/firebasejs/10.7.0/firebase-database-compat.js"></script>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f5f7fa; }
.header {
    background: linear-gradient(135deg, #00205B 0%, #003380 100%);
    color: white; padding: 14px 20px;
    display: flex; align-items: center; gap: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.header-icon { font-size: 24px; }
.header-info h1 { font-size: 16px; font-weight: 700; }
.header-info p { font-size: 12px; opacity: 0.8; }
.status-bar {
    padding: 10px 20px; display: flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 600;
}
.status-bar.en_curso { background: #E8F5E9; color: #2E7D32; }
.status-bar.finalizado { background: #F5F5F5; color: #757575; }
.status-bar.no_iniciado { background: #FFF8E1; color: #F57F17; }
.pulse { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.pulse.en_curso { background: #4CAF50; animation: pulse-anim 1.5s infinite; }
.pulse.finalizado { background: #9E9E9E; }
.pulse.no_iniciado { background: #FFC107; }
@keyframes pulse-anim {
    0% { box-shadow: 0 0 0 0 rgba(76,175,80,0.5); }
    70% { box-shadow: 0 0 0 10px rgba(76,175,80,0); }
    100% { box-shadow: 0 0 0 0 rgba(76,175,80,0); }
}
#map { width: 100%; height: calc(100vh - 105px); }
.info-overlay {
    position: absolute; bottom: 20px; left: 20px; right: 20px; z-index: 1000;
    background: white; border-radius: 14px; padding: 12px 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    display: flex; justify-content: space-between; align-items: center;
}
.info-overlay .label { font-size: 10px; color: #888; text-transform: uppercase; }
.info-overlay .value { font-size: 13px; font-weight: 700; color: #00205B; }
</style>
</head>
<body>
<div class="header">
    <span class="header-icon">&#x1F68D;</span>
    <div class="header-info">
        <h1>${'$'}rutaEscaped</h1>
        <p>${'$'}fechaEscaped &bull; Salida: ${'$'}horarioEscaped</p>
    </div>
</div>
<div id="statusBar" class="status-bar no_iniciado">
    <span id="statusDot" class="pulse no_iniciado"></span>
    <span id="statusText">Conectando...</span>
</div>
<div id="map"></div>
<div class="info-overlay">
    <div><div class="label">Ruta</div><div class="value">${'$'}rutaEscaped</div></div>
    <div><div class="label">Horario</div><div class="value">${'$'}horarioEscaped</div></div>
    <div id="updateInfo" style="text-align:right"><div class="label">Distancia</div><div class="value" id="infoDistancia">--</div></div>
    <div style="text-align:right"><div class="label">Estado</div><div class="value" id="infoEstado">--</div></div>
</div>
<script>
var firebaseConfig = {
    apiKey: "AIzaSyCmEhc4OB_hRFfGY6ikFXD4VOZwMvI47FA",
    authDomain: "smartvan-38132.firebaseapp.com",
    databaseURL: "https://smartvan-38132-default-rtdb.firebaseio.com",
    projectId: "smartvan-38132",
    storageBucket: "smartvan-38132.firebasestorage.app",
    appId: "1:626527596089:web:f060ae97f6bc101b4e27e8"
};
firebase.initializeApp(firebaseConfig);

var map = L.map('map').setView([20.5888, -100.3899], 7);
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap', maxZoom: 19
}).addTo(map);

function makeIcon(bg, label, size, border) {
    return L.divIcon({
        html: '<div style="background:'+bg+';color:white;border-radius:50%;width:'+size+'px;height:'+size+'px;display:flex;align-items:center;justify-content:center;font-size:'+(size>30?20:12)+'px;font-weight:bold;box-shadow:0 2px 10px rgba(0,0,0,0.4);border:3px solid '+(border||'white')+';">'+label+'</div>',
        className: '', iconSize: [size, size], iconAnchor: [size/2, size/2]
    });
}

var vanIcon = makeIcon('#00205B', '&#x1F68C;', 40, '#00ACC1');
var startIcon = makeIcon('#4CAF50', 'A', 28, 'white');
var endIcon = makeIcon('#EF5350', 'B', 28, 'white');
var stopIcon = makeIcon('#FF9800', '&#x25CF;', 22, 'white');

var marker = null;
var trail = [];
var trailLine = L.polyline([], {color: '#00ACC1', weight: 3, opacity: 0.7}).addTo(map);
var routeLine = null;

var ruta = '${rutaEscaped}';
var fecha = '${fechaEscaped}';
var horario = '${horarioEscaped}';

// ====== OSRM: Trazar ruta real por carretera ======
function trazarRutaOSRM(waypoints) {
    if (waypoints.length < 2) return;
    var coordStr = waypoints.map(function(wp) { return wp.lng + ',' + wp.lat; }).join(';');
    var url = 'https://router.project-osrm.org/route/v1/driving/' + coordStr + '?overview=full&geometries=geojson';
    fetch(url).then(function(r) { return r.json(); }).then(function(data) {
        if (data.routes && data.routes.length > 0) {
            var geojson = data.routes[0].geometry;
            if (routeLine) map.removeLayer(routeLine);
            routeLine = L.geoJSON(geojson, { style: { color: '#00205B', weight: 5, opacity: 0.6 } }).addTo(map);
            var distKm = (data.routes[0].distance / 1000).toFixed(0);
            var durMin = (data.routes[0].duration / 60).toFixed(0);
            document.getElementById('infoDistancia').textContent = distKm + ' km (~' + durMin + ' min)';
        }
    }).catch(function(err) {
        // Fallback: linea recta si OSRM no responde
        var coords = waypoints.map(function(wp) { return [wp.lat, wp.lng]; });
        L.polyline(coords, {color: '#00205B', weight: 4, opacity: 0.4, dashArray: '12,8'}).addTo(map);
    });
}

// ====== CARGAR WAYPOINTS + TRAZAR RUTA ======
var rutaRef = firebase.database().ref('catalogos/rutas/' + ruta + '/waypoints');
rutaRef.once('value', function(snap) {
    var waypoints = snap.val();
    if (!waypoints || !Array.isArray(waypoints)) return;
    if (waypoints.length < 2) return;

    // Marcadores de paradas
    L.marker([waypoints[0].lat, waypoints[0].lng], {icon: startIcon}).addTo(map)
        .bindPopup('<b>Salida</b><br>' + (waypoints[0].nombre || 'Origen'));
    L.marker([waypoints[waypoints.length-1].lat, waypoints[waypoints.length-1].lng], {icon: endIcon}).addTo(map)
        .bindPopup('<b>Destino</b><br>' + (waypoints[waypoints.length-1].nombre || 'Destino'));
    for (var i = 1; i < waypoints.length - 1; i++) {
        if (waypoints[i].nombre) {
            L.marker([waypoints[i].lat, waypoints[i].lng], {icon: stopIcon}).addTo(map)
                .bindPopup('<b>Parada</b><br>' + waypoints[i].nombre);
        }
    }

    // Trazar ruta real por OSRM
    trazarRutaOSRM(waypoints);

    // Ajustar vista
    var coords = waypoints.map(function(wp) { return [wp.lat, wp.lng]; });
    map.fitBounds(L.latLngBounds(coords).pad(0.15));
});

// ====== RASTREO EN VIVO ======
var rastreoRef = firebase.database().ref('viajes/' + ruta + '/' + fecha + '/' + horario + '/rastreo');
var statusLabels = { 'en_curso': 'En camino', 'finalizado': 'Viaje finalizado', 'no_iniciado': 'Esperando inicio' };

rastreoRef.on('value', function(snapshot) {
    var data = snapshot.val();
    if (!data) {
        document.getElementById('statusText').textContent = 'El viaje aun no ha iniciado';
        document.getElementById('infoEstado').textContent = 'Esperando';
        return;
    }
    var estado = data.estado || 'no_iniciado';
    var lat = data.lat || 0;
    var lng = data.lng || 0;

    document.getElementById('statusBar').className = 'status-bar ' + estado;
    document.getElementById('statusDot').className = 'pulse ' + estado;
    document.getElementById('statusText').textContent = statusLabels[estado] || estado;
    document.getElementById('infoEstado').textContent = statusLabels[estado] || estado;

    if (lat !== 0 && lng !== 0) {
        var pos = [lat, lng];
        if (!marker) {
            marker = L.marker(pos, {icon: vanIcon}).addTo(map);
            map.setView(pos, 14);
        } else {
            marker.setLatLng(pos);
        }
        trail.push(pos);
        if (trail.length > 500) trail = trail.slice(-500);
        trailLine.setLatLngs(trail);
        if (estado === 'en_curso') map.panTo(pos);
    }
    if (data.ultimaActualizacion) {
        var ms = parseInt(data.ultimaActualizacion);
        if (!isNaN(ms)) {
            var d = new Date(ms);
            var t = d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0') + ':' + d.getSeconds().toString().padStart(2,'0');
            document.getElementById('statusText').textContent = statusLabels[estado] + ' • ' + t;
        }
    }
});
</script>
</body>
</html>
""".trimIndent()

    // Embeber como iframe en vez de popup (evita bloqueadores)
    val iframe = window.document.createElement("iframe") as org.w3c.dom.HTMLIFrameElement
    iframe.id = "smartvan-map-frame"
    iframe.style.position = "fixed"
    iframe.style.top = "0"
    iframe.style.left = "0"
    iframe.style.width = "100%"
    iframe.style.height = "100%"
    iframe.style.border = "none"
    iframe.style.zIndex = "99999"
    iframe.style.backgroundColor = "#f5f7fa"

    // Botón cerrar flotante
    val closeBtn = window.document.createElement("button") as org.w3c.dom.HTMLButtonElement
    closeBtn.id = "smartvan-map-close"
    closeBtn.textContent = "✕ Cerrar Mapa"
    closeBtn.style.position = "fixed"
    closeBtn.style.top = "10px"
    closeBtn.style.right = "10px"
    closeBtn.style.zIndex = "100000"
    closeBtn.style.padding = "10px 20px"
    closeBtn.style.border = "none"
    closeBtn.style.borderRadius = "24px"
    closeBtn.style.background = "#EF5350"
    closeBtn.style.color = "white"
    closeBtn.style.fontWeight = "bold"
    closeBtn.style.fontSize = "14px"
    closeBtn.style.cursor = "pointer"
    closeBtn.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)"
    closeBtn.onclick = {
        window.document.getElementById("smartvan-map-frame")?.remove()
        window.document.getElementById("smartvan-map-close")?.remove()
        null
    }

    window.document.body?.appendChild(iframe)
    window.document.body?.appendChild(closeBtn)

    // Escribir HTML en el iframe
    val iframeDoc = iframe.contentDocument ?: iframe.contentWindow?.document
    iframeDoc?.open()
    iframeDoc?.write(htmlMapa)
    iframeDoc?.close()
}

actual fun abrirVistaPreviaRuta(rutaNombre: String, waypointsJson: String) {
    val nombreEscaped = rutaNombre.replace("'", "\\'")
    val html = """
<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Vista Previa - $rutaNombre</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
.header {
    background: linear-gradient(135deg, #00205B 0%, #003380 100%);
    color: white; padding: 14px 20px;
    display: flex; align-items: center; justify-content: space-between;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.header h1 { font-size: 16px; font-weight: 700; }
.header .badge { background: #00ACC1; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; }
#map { width: 100%; height: calc(100vh - 52px); }
.legend {
    position: absolute; bottom: 20px; left: 20px; z-index: 1000;
    background: white; border-radius: 12px; padding: 12px 16px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15); font-size: 12px;
}
.legend-item { display: flex; align-items: center; gap: 8px; margin: 4px 0; }
.legend-dot { width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
.info-panel {
    position: absolute; top: 62px; right: 10px; z-index: 1000;
    background: white; border-radius: 12px; padding: 12px 16px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15); font-size: 12px;
    max-width: 240px; max-height: calc(100vh - 120px); overflow-y: auto;
}
.info-panel h3 { font-size: 13px; color: #00205B; margin-bottom: 6px; }
.wp-item { padding: 3px 0; border-bottom: 1px solid #f0f0f0; }
.wp-item:last-child { border-bottom: none; }
.wp-idx { display: inline-block; width: 20px; height: 20px; border-radius: 50%;
    text-align: center; line-height: 20px; color: white; font-weight: bold; font-size: 10px; margin-right: 6px; }
.click-hint {
    position: absolute; top: 62px; left: 50%; transform: translateX(-50%); z-index: 1000;
    background: #00205B; color: white; padding: 8px 18px; border-radius: 20px;
    font-size: 13px; font-weight: 600; box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    pointer-events: none; transition: opacity 0.5s;
}
</style>
</head>
<body>
<div class="header">
    <div><h1>&#x1F5FA; $rutaNombre</h1></div>
    <div style="display:flex;gap:8px;align-items:center;">
        <span class="badge" id="distBadge">--</span>
        <span class="badge">ADMIN</span>
    </div>
</div>
<div id="map"></div>
<div id="clickHint" class="click-hint">&#x1F446; Haz click en el mapa para agregar puntos</div>
<div class="legend">
    <div style="font-weight:bold;margin-bottom:4px;">Leyenda</div>
    <div class="legend-item"><div class="legend-dot" style="background:#4CAF50;"></div> Salida (A)</div>
    <div class="legend-item"><div class="legend-dot" style="background:#FF9800;"></div> Parada intermedia</div>
    <div class="legend-item"><div class="legend-dot" style="background:#EF5350;"></div> Destino (B)</div>
    <div class="legend-item"><div style="width:30px;height:3px;background:#00205B;"></div> Ruta por carretera</div>
</div>
<div id="infoPanel" class="info-panel"></div>
<script>
var waypoints = $waypointsJson;
var markers = [];
var routeLine = null;

var map = L.map('map').setView([20.5, -100.0], 7);
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap', maxZoom: 19
}).addTo(map);

function makeIcon(color, label, size) {
    return L.divIcon({
        html: '<div style="background:'+color+';color:white;border-radius:50%;width:'+size+'px;height:'+size+'px;display:flex;align-items:center;justify-content:center;font-size:'+(size>26?14:11)+'px;font-weight:bold;box-shadow:0 2px 8px rgba(0,0,0,0.35);border:3px solid white;">'+label+'</div>',
        className: '', iconSize: [size, size], iconAnchor: [size/2, size/2]
    });
}

// ====== OSRM: Trazar ruta real ======
function trazarRuta() {
    if (waypoints.length < 2) {
        if (routeLine) { map.removeLayer(routeLine); routeLine = null; }
        document.getElementById('distBadge').textContent = '--';
        return;
    }
    var coordStr = waypoints.map(function(wp) { return wp.lng + ',' + wp.lat; }).join(';');
    var url = 'https://router.project-osrm.org/route/v1/driving/' + coordStr + '?overview=full&geometries=geojson';
    fetch(url).then(function(r) { return r.json(); }).then(function(data) {
        if (data.routes && data.routes.length > 0) {
            if (routeLine) map.removeLayer(routeLine);
            routeLine = L.geoJSON(data.routes[0].geometry, {
                style: { color: '#00205B', weight: 5, opacity: 0.6 }
            }).addTo(map);
            var km = (data.routes[0].distance / 1000).toFixed(0);
            var min = (data.routes[0].duration / 60).toFixed(0);
            document.getElementById('distBadge').textContent = km + ' km (~' + min + ' min)';
        }
    }).catch(function() {
        // Fallback linea recta
        if (routeLine) map.removeLayer(routeLine);
        var coords = waypoints.map(function(wp) { return [wp.lat, wp.lng]; });
        routeLine = L.polyline(coords, {color: '#00205B', weight: 4, opacity: 0.4, dashArray: '12,8'}).addTo(map);
    });
}

// ====== Redibujar marcadores ======
function redibujarMarcadores() {
    markers.forEach(function(m) { map.removeLayer(m); });
    markers = [];
    waypoints.forEach(function(wp, i) {
        var color, label, size;
        if (wp.tipo === 'subida') { color = '#4CAF50'; size = 30; }
        else if (wp.tipo === 'bajada') { color = '#EF5350'; size = 30; }
        else if (i === 0) { color = '#4CAF50'; size = 32; }
        else if (i === waypoints.length - 1) { color = '#EF5350'; size = 32; }
        else { color = '#FF9800'; size = 26; }
        label = (i+1).toString();
        var m = L.marker([wp.lat, wp.lng], {icon: makeIcon(color, label, size), draggable: true}).addTo(map);
        m.bindPopup('<b>' + wp.nombre + '</b><br>' + wp.lat.toFixed(5) + ', ' + wp.lng.toFixed(5));
        // Drag para reposicionar
        m.on('dragend', function(e) {
            var pos = e.target.getLatLng();
            waypoints[i].lat = Math.round(pos.lat * 100000) / 100000;
            waypoints[i].lng = Math.round(pos.lng * 100000) / 100000;
            actualizarPanel();
            trazarRuta();
            enviarWaypointsAlPadre();
        });
        markers.push(m);
    });
}

// ====== Panel lateral ======
function actualizarPanel() {
    var h = '<h3>Puntos (' + waypoints.length + ')</h3>';
    if (waypoints.length === 0) {
        h += '<p style="color:#888;">Haz click en el mapa para agregar el punto de salida.</p>';
    }
    waypoints.forEach(function(wp, i) {
        var bg = wp.tipo === 'subida' ? '#4CAF50' : (wp.tipo === 'bajada' ? '#EF5350' : (i === 0 ? '#4CAF50' : (i === waypoints.length-1 ? '#EF5350' : '#FF9800')));
        var lbl = (i+1);
        h += '<div class="wp-item" style="display:flex;align-items:center;justify-content:space-between;">';
        h += '<span><span class="wp-idx" style="background:'+bg+';">'+lbl+'</span>' + wp.nombre + '</span>';
        h += '<span style="cursor:pointer;color:#EF5350;font-weight:bold;font-size:16px;" onclick="eliminarPunto('+i+')">&times;</span>';
        h += '</div>';
    });
    document.getElementById('infoPanel').innerHTML = h;
}

// ====== Click en mapa para agregar puntos ======
map.on('click', function(e) {
    var lat = Math.round(e.latlng.lat * 100000) / 100000;
    var lng = Math.round(e.latlng.lng * 100000) / 100000;
    var nombre = prompt('Nombre del punto (ej: San Juan del Rio):', 'Punto ' + (waypoints.length + 1));
    if (nombre === null) return;
    if (nombre.trim() === '') nombre = 'Punto ' + (waypoints.length + 1);
    waypoints.push({lat: lat, lng: lng, nombre: nombre.trim()});
    redibujarMarcadores();
    actualizarPanel();
    trazarRuta();
    enviarWaypointsAlPadre();
    // Ocultar hint despues del primer click
    var hint = document.getElementById('clickHint');
    if (hint) hint.style.opacity = '0';
});

// Eliminar punto (llamado desde el panel)
window.eliminarPunto = function(idx) {
    waypoints.splice(idx, 1);
    redibujarMarcadores();
    actualizarPanel();
    trazarRuta();
    enviarWaypointsAlPadre();
};

// ====== Comunicar cambios al padre (Compose) ======
function enviarWaypointsAlPadre() {
    try {
        window.parent.postMessage({type: 'smartvan-waypoints', waypoints: waypoints}, '*');
    } catch(e) {}
}

// ====== Inicializar con waypoints existentes ======
if (waypoints.length > 0) {
    redibujarMarcadores();
    trazarRuta();
    var coords = waypoints.map(function(wp) { return [wp.lat, wp.lng]; });
    map.fitBounds(L.latLngBounds(coords).pad(0.15));
    document.getElementById('clickHint').style.opacity = '0';
}
actualizarPanel();
</script>
</body>
</html>
""".trimIndent()

    // Embeber como iframe en vez de popup
    val iframe = window.document.createElement("iframe") as org.w3c.dom.HTMLIFrameElement
    iframe.id = "smartvan-preview-frame"
    iframe.style.position = "fixed"
    iframe.style.top = "0"
    iframe.style.left = "0"
    iframe.style.width = "100%"
    iframe.style.height = "100%"
    iframe.style.border = "none"
    iframe.style.zIndex = "99999"
    iframe.style.backgroundColor = "#f5f7fa"

    val closeBtn = window.document.createElement("button") as org.w3c.dom.HTMLButtonElement
    closeBtn.id = "smartvan-preview-close"
    closeBtn.textContent = "✕ Cerrar"
    closeBtn.style.position = "fixed"
    closeBtn.style.top = "10px"
    closeBtn.style.right = "10px"
    closeBtn.style.zIndex = "100000"
    closeBtn.style.padding = "10px 20px"
    closeBtn.style.border = "none"
    closeBtn.style.borderRadius = "24px"
    closeBtn.style.background = "#EF5350"
    closeBtn.style.color = "white"
    closeBtn.style.fontWeight = "bold"
    closeBtn.style.fontSize = "14px"
    closeBtn.style.cursor = "pointer"
    closeBtn.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)"
    closeBtn.onclick = {
        window.document.getElementById("smartvan-preview-frame")?.remove()
        window.document.getElementById("smartvan-preview-close")?.remove()
        null
    }

    window.document.body?.appendChild(iframe)
    window.document.body?.appendChild(closeBtn)

    val iframeDoc = iframe.contentDocument ?: iframe.contentWindow?.document
    iframeDoc?.open()
    iframeDoc?.write(html)
    iframeDoc?.close()

    // Escuchar mensajes del iframe (waypoints actualizados por click/drag)
    window.addEventListener("message", { event: dynamic ->
        try {
            val data = event.data
            if (data != null && data != undefined && data.type == "smartvan-waypoints") {
                val wps = data.waypoints
                if (wps != null && wps != undefined) {
                    // Guardar waypoints actualizados en variable global para que el admin los recupere
                    js("window.__smartvanUpdatedWaypoints = wps")
                }
            }
        } catch (e: Exception) {}
    })
}

actual fun obtenerWaypointsActualizados(): List<Triple<Double, Double, String>>? {
    return try {
        val wps = js("window.__smartvanUpdatedWaypoints")
        if (wps == null || wps == undefined) return null
        val length = (wps.length as Number).toInt()
        if (length == 0) return null
        val result = mutableListOf<Triple<Double, Double, String>>()
        for (i in 0 until length) {
            val wp = wps[i]
            val lat = (wp.lat as Number).toDouble()
            val lng = (wp.lng as Number).toDouble()
            val nombre = (wp.nombre as? String) ?: "Punto ${i + 1}"
            result.add(Triple(lat, lng, nombre))
        }
        // Limpiar después de leer
        js("window.__smartvanUpdatedWaypoints = null")
        result
    } catch (e: Exception) {
        null
    }
}

actual fun cerrarMapaPreview() {
    window.document.getElementById("smartvan-preview-frame")?.remove()
    window.document.getElementById("smartvan-preview-close")?.remove()
    window.document.getElementById("smartvan-map-frame")?.remove()
    window.document.getElementById("smartvan-map-close")?.remove()
}

// Cache de ubicación GPS con watchPosition para stream continuo
private var ultimaLatitud: Double? = null
private var ultimaLongitud: Double? = null
private var watchId: Int? = null

private fun iniciarWatchGPS() {
    if (watchId != null) return // Ya está corriendo
    val geolocation = window.navigator.asDynamic().geolocation
    if (geolocation == null || geolocation == undefined) {
        return
    }
    watchId = (geolocation.watchPosition(
        { position: dynamic ->
            ultimaLatitud = (position.coords.latitude as Number).toDouble()
            ultimaLongitud = (position.coords.longitude as Number).toDouble()
        },
        { error: dynamic ->
        },
        js("({enableHighAccuracy: true, timeout: 30000, maximumAge: 10000})")
    ) as Number).toInt()
}

private var wakeLockSentinel: dynamic = null

actual fun mantenerPantallaActiva(activo: Boolean) {
    try {
        val nav = window.navigator.asDynamic()
        if (activo) {
            val wl = nav.wakeLock
            if (wl != null && wl != undefined) {
                wl.request("screen").then({ sentinel -> wakeLockSentinel = sentinel })
            }
        } else {
            wakeLockSentinel?.release()
            wakeLockSentinel = null
        }
    } catch (e: Throwable) { /* best effort — Wake Lock API no siempre disponible */ }
}

actual suspend fun obtenerUbicacionActual(): Pair<Double, Double>? {
    return try {
        // Iniciar watch si no está corriendo
        iniciarWatchGPS()

        // Si ya tenemos ubicación cacheada, retornarla inmediatamente
        val lat = ultimaLatitud
        val lng = ultimaLongitud
        if (lat != null && lng != null) {
            return Pair(lat, lng)
        }

        // Si no hay cache, intentar getCurrentPosition como fallback
        kotlinx.coroutines.suspendCancellableCoroutine { cont ->
            val geolocation = window.navigator.asDynamic().geolocation
            if (geolocation == null || geolocation == undefined) {
                cont.resume(null, null)
                return@suspendCancellableCoroutine
            }
            geolocation.getCurrentPosition(
                { position: dynamic ->
                    val newLat = (position.coords.latitude as Number).toDouble()
                    val newLng = (position.coords.longitude as Number).toDouble()
                    ultimaLatitud = newLat
                    ultimaLongitud = newLng
                    cont.resume(Pair(newLat, newLng), null)
                },
                { error: dynamic ->
                    // Intentar con baja precisión como último recurso
                    geolocation.getCurrentPosition(
                        { position: dynamic ->
                            val newLat = (position.coords.latitude as Number).toDouble()
                            val newLng = (position.coords.longitude as Number).toDouble()
                            ultimaLatitud = newLat
                            ultimaLongitud = newLng
                            if (cont.isActive) cont.resume(Pair(newLat, newLng), null)
                        },
                        { err2: dynamic ->
                            if (cont.isActive) cont.resume(null, null)
                        },
                        js("({enableHighAccuracy: false, timeout: 15000, maximumAge: 60000})")
                    )
                },
                js("({enableHighAccuracy: true, timeout: 15000, maximumAge: 10000})")
            )
        }
    } catch (e: Exception) {
        null
    }
}

@Composable
actual fun QRScannerScreen(
    onCodigoEscaneado: (String) -> Unit,
    onCerrar: () -> Unit
) {
    var codigoManual by androidx.compose.runtime.mutableStateOf("")

    androidx.compose.foundation.layout.Column(
        modifier = androidx.compose.ui.Modifier.fillMaxSize().padding(24.dp),
        horizontalAlignment = androidx.compose.ui.Alignment.CenterHorizontally,
        verticalArrangement = androidx.compose.foundation.layout.Arrangement.Center
    ) {
        androidx.compose.material3.Text(
            "Verificación Manual",
            style = androidx.compose.material3.MaterialTheme.typography.titleLarge,
            fontWeight = androidx.compose.ui.text.font.FontWeight.Bold
        )
        androidx.compose.foundation.layout.Spacer(modifier = androidx.compose.ui.Modifier.height(8.dp))
        androidx.compose.material3.Text(
            "La cámara no está disponible en Web. Ingresa el código QR manualmente.",
            color = androidx.compose.ui.graphics.Color.Gray,
            style = androidx.compose.material3.MaterialTheme.typography.bodySmall
        )
        androidx.compose.foundation.layout.Spacer(modifier = androidx.compose.ui.Modifier.height(20.dp))
        androidx.compose.material3.OutlinedTextField(
            value = codigoManual,
            onValueChange = { codigoManual = it },
            label = { androidx.compose.material3.Text("Código QR") },
            placeholder = { androidx.compose.material3.Text("SMARTVAN-...") },
            singleLine = true,
            modifier = androidx.compose.ui.Modifier.fillMaxWidth()
        )
        androidx.compose.foundation.layout.Spacer(modifier = androidx.compose.ui.Modifier.height(16.dp))
        androidx.compose.material3.Button(
            onClick = { if (codigoManual.isNotBlank()) onCodigoEscaneado(codigoManual.trim()) },
            modifier = androidx.compose.ui.Modifier.fillMaxWidth(),
            enabled = codigoManual.isNotBlank()
        ) {
            androidx.compose.material3.Text("Verificar Código")
        }
        androidx.compose.foundation.layout.Spacer(modifier = androidx.compose.ui.Modifier.height(8.dp))
        androidx.compose.material3.TextButton(onClick = onCerrar) {
            androidx.compose.material3.Text("Cancelar")
        }
    }
}
